import { Component, OnInit, ViewChild } from "@angular/core";
import { FormBuilder } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { GridReadyEvent, FirstDataRenderedEvent } from "ag-grid-community";
import { NgxUiLoaderService } from "ngx-ui-loader";
import { JsignalService } from "src/app/shared/services/jsignal.service";
import { LocalStorageSettingsService } from "src/app/shared/services/local-storage-settings.service";
import { CreateSignalPopupComponent } from "../jsignal-product/review-activities/create-signal-popup/create-signal-popup.component";
import { WorkflowStep } from "../workflow-steps/workflow-steps.component";
import { JSIGNALBUTTON } from "src/app/shared/common/JsignalButtons";
import { SignalAttachmentComponent } from "./signal-attachment/signal-attachment.component";
import { SignalDecisionComponent } from "./signal-decision/signal-decision.component";
import { SignalDetailsComponent } from "./signal-details/signal-details.component";
import { CommonWorkflowDialogComponent } from "../workflow-steps/common-workflow-dialog/common-workflow-dialog.component";
import { AgGridActionbutton } from "src/app/shared/common/AgGridActionbutton";
import { GenericBadgeRendererComponent } from "src/app/shared/common/generic-badge-renderer.component";
import { constants } from "src/app/shared/common/contants";
import { Router } from "@angular/router";

// ===== Hardcoded Validation Models =====
interface ValidationError {
  component: string;
  field?: string;
  message: string;
}

interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
}

interface WorkflowOption {
  value: string;
  displayName: string;
  nextStep: number | null;
}

@Component({
    selector: "app-signals",
    templateUrl: "./signals.component.html",
    styleUrls: ["./signals.component.scss"],
    standalone: false
})
export class SignalsComponent implements OnInit {
  @ViewChild(SignalDetailsComponent, { static: false })
  detailsComponent!: SignalDetailsComponent;

  @ViewChild(SignalDecisionComponent, { static: false })
  decisionComponent!: SignalDecisionComponent;

  @ViewChild(SignalAttachmentComponent, { static: false })
  attachmentComponent!: SignalAttachmentComponent;

  components: any;
  columnDefs: any;
  defaultColDef: any;
  rowData: any = [];
  gridApi: any;
  totalCount: any;
  totalSignalValidated: any;
  signalAssessed: any;
  signalCommunicated: any;
  noRowsMessage = 'No signals found. Click "Create" to add a new signal.';
  pageTitle = "Signals";
  isEditMode = false;

  steps: WorkflowStep[] = [
    { label: "Signal Detection", stepId: 1 },
    { label: "Signal Validation", stepId: 2 },
    { label: "Signal Assessment", stepId: 3 },
    { label: "Signal Communication", stepId: 4 },
  ];

  // ===== Validation State =====
  validationErrors: ValidationError[] = [];
  showValidationModal = false;

  // ===== Status Modal =====
  showStatusModal = false;
  statusOptions: WorkflowOption[] = [];
  selectedStatus = "";

  // ===== Workflow Configuration =====
  workflowSteps: any = {
    1: {
      options: [
        { value: "VALIDATED", displayName: "Validated", nextStep: 2 },
        {
          value: "NON_VALIDATED",
          displayName: "Non Validated",
          nextStep: null,
        },
      ],
    },
    2: {
      options: [
        {
          value: "IDENTIFIED_RISK_ADR",
          displayName: "Identified Risk (ADR)",
          nextStep: 3,
        },
        { value: "POTENTIAL_RISK", displayName: "Potential Risk", nextStep: 3 },
        { value: "REFUTED", displayName: "Refuted", nextStep: null },
      ],
    },
    3: {
      options: [
        {
          value: "IMPORTANT_IDENTIFIED_RISK",
          displayName: "Important Identified Risk",
          nextStep: 4,
        },
        {
          value: "INACTIVE_IDENTIFIED_RISK",
          displayName: "Inactive Identified Risk",
          nextStep: null,
        },
      ],
    },
    4: {
      options: [
        { value: "COMPLETED", displayName: "Completed", nextStep: null },
      ],
    },
  };

  activeIndex = 0;
  completedSteps: number[] = [];
  showWorkflow = false;
  selectedSignal: any;
  selectedTabIndex = 0;
  isSidenavOpen=false;
  totaldetectedCount: any;

  queryData:any;
  constructor(
    private snackBar: MatSnackBar,
    private spinner: NgxUiLoaderService,
    private jsignalsService: JsignalService,
    private localStorageSettings: LocalStorageSettingsService,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private constants: constants,
    private router: Router
  ) {
    this.components = {
      jsignalButton: JSIGNALBUTTON,
      buttonRenderer: AgGridActionbutton,
    };
  }

  ngOnInit(): void {
    this.initializeGrid();
    this.loadGridData();
    this.getSignalCounts();
    this.queryData=history.state?.selectedRowData
  }

  enableEdit() {
    this.isEditMode = true;
  }

  saveDetails() {
    this.detailsComponent.saveDetails();
    this.isEditMode = false;
  }

  cancelEdit() {
    this.detailsComponent.cancelEdit();
    this.isEditMode = false;
  }

  initializeGrid() {
    this.columnDefs = [
      {
        headerName: "Action",
        sortable: false,
        filter: false,
        resizable: true,
        width: 200,
        cellRenderer: "buttonRenderer",
        pinned: "right",
        lockPinned: true,
        cellRendererParams: (params: any) => ({
          userAssign: () => this.userAssign(params),
          edit: () => this.EditIcon(params),
          delete: () => this.DeleteIcon(params),
          // startWorkflow: () => this.startWorkflow(params),
        }),
      },
      {
        headerName: "Signal ID",
        field: "signalId",
        width: 180,
        cellRenderer: 'jsignalButton',
        cellRendererParams: {
          signalLink: true,
          onSignalClick: (params: any) => {

                if (params.rowData.status === "No") {
                  this.snackBar.open(this.constants.deleterowMsg, "OK", { duration: 5000 });
                  return;
                }

            if (!params.rowData.assignedUserId) {
              this.snackBar.open(
                "Please self assign before proceeding to workflow",
                "Ok",
                { duration: 3000 }
              );
              return;
            }

            if (params.rowData.signalStatus == "COMPLETED") {
              this.snackBar.open(
                "Signal workflow already completed. No further actions allowed.",
                "Ok",
                { duration: 3000 }
              );
              return;
            }

            this.selectedSignal = params.rowData;

            // ✅ Completed Steps
            let stepArray: number[] = [];

            if (this.selectedSignal.completedSteps) {
              stepArray = this.selectedSignal.completedSteps
                .split(',')
                .map((s: string) => +s);
            }

            this.completedSteps = stepArray.map((stepId: number) =>
              this.steps.findIndex(s => s.stepId === stepId)
            );

                          // Show workflow FIRST
              this.showWorkflow = true;

              // ✅ Determine active step properly
              let stepIndex = 0;

              // Case 1: If backend gives currentStepId
              if (this.selectedSignal.currentStepId) {
                const index = this.steps.findIndex(
                  s => s.stepId === this.selectedSignal.currentStepId
                );

                stepIndex = index !== -1 ? index : 0;
              }

              // Case 2: If currentStepId is NULL → derive from completedSteps
              else if (stepArray.length > 0) {
                const lastCompletedStepId = Math.max(...stepArray);

                const nextIndex = this.steps.findIndex(
                  s => s.stepId === lastCompletedStepId
                );

                // Move to next step
                stepIndex = nextIndex !== -1 ? nextIndex + 1 : 0;
              }

              // Case 3: nothing → default
              else {
                stepIndex = 0;
              }

              // ✅ Apply after render
              setTimeout(() => {
                this.activeIndex = stepIndex;
              });

              console.log("Resolved Active Step Index:", stepIndex);

            console.log("Active Step:", this.activeIndex);
            console.log("Completed Steps:", this.completedSteps);

            this.pageTitle = "Signal Review";
          },
        },
        ...this.jsignalsService.autoTooltip("Signal ID"),
      },


      {
        headerName: "Signal Name",
        field: "signalName",
        width: 250,
        ...this.jsignalsService.autoTooltip("Signal Name"),
      },
      // {
      //   headerName: "Type",
      //   field: "type",
      //   width: 220,
      //   ...this.jsignalsService.autoTooltip("Type"),
      // },
      {
        headerName: "Product",
        field: "product",
        width: 220,
        ...this.jsignalsService.autoTooltip("Product"),
      },
      {
        headerName: "Medical Concept",
        field: "medicalConcept",
        width: 220,
        ...this.jsignalsService.autoTooltip("Medical Concept"),
      },
      {
        headerName: "Signal Status",
        field: "signalStatus",
        width: 220,
        ...this.jsignalsService.autoTooltip("Signal Status"),
      },
      {
        headerName: "Signal Detected Date",
        field: "signalDetectedDate",
        width: 180,
        ...this.jsignalsService.autoTooltip("Signal Detected Date"),
      },
      {
        headerName: "Signal Created Date",
        field: "signalCreationDate",
        width: 180,
        ...this.jsignalsService.autoTooltip("Signal Created Date"),
      },
          {
            headerName: 'Status',
             width:120,
            field: 'status',
             tooltipValueGetter: (params) => 'Status : ' + params.value,
             cellRenderer: GenericBadgeRendererComponent,
              cellRendererParams: { type: 'YES_NO' }
          },
      {
        headerName: "Assigned User",
        field: "assignedUserName",
        width: 250,
        ...this.jsignalsService.autoTooltip("Assigned User"),
      },
      {
        headerName: "Primary Source",
        field: "primarySource",
        width: 250,
        ...this.jsignalsService.autoTooltip("Primary Source"),
      },
      {
        headerName: "Secondary Source",
        field: "secondarySource",
        width: 250,
        ...this.jsignalsService.autoTooltip("Secondary Source"),
      },
      {
        headerName: "Description",
        field: "signalDescription",
        width: 300,
        ...this.jsignalsService.autoTooltip("Description"),
      },
      {
        headerName: "Created By",
        field: "createdBy",
        width: 200,
        ...this.jsignalsService.autoTooltip("Created By"),
      },
      {
        headerName: "Created Date",
        field: "createdDateTime",
        width: 180,
        ...this.jsignalsService.autoTooltip("Created Date"),
        valueFormatter: (params: any) => {
          return params.value
            ? new Date(params.value).toLocaleDateString()
            : "";
        },
      },
      {
        headerName: "Last Modified By",
        field: "lastModifiedBy",
        width: 180,
        ...this.jsignalsService.autoTooltip("Last Modified By"),
      },
      {
        headerName: "Last Modified At",
        field: "lastModifiedDate",
        width: 180,
        ...this.jsignalsService.autoTooltip("Last Modified At"),
        // valueFormatter: (params: any) => {
        //   return params.value
        //     ? new Date(params.value).toLocaleDateString()
        //     : "";
        // },
      },
      {
        headerName: "Signal Type",
        field: "signalType",
        width: 180,
        ...this.jsignalsService.autoTooltip("Signal Type"),
      },
    ];

    this.defaultColDef = {
filterParams: {
        maxNumConditions: 2,
        numAlwaysVisibleConditions: 2,
        defaultJoinOperator: 'OR'
      },
      sortable: true,
      filter: true,
      resizable: true,
    };
  }

  EditIcon(params: any) {
                    if (params.data.status === "No") {
                  this.snackBar.open(this.constants.deleterowMsg, "OK", { duration: 5000 });
                  return;
                }
    this.selectedSignal = params.data;
    console.log("selected Signal ", this.selectedSignal);

    this.dialog
      .open(CreateSignalPopupComponent, {
        maxWidth: "100vw",
        height: "95%",
        width: "90%",
        disableClose: true,
        data: {
          mode: "edit",
          signalData: this.selectedSignal,
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.updateSignal(this.selectedSignal.id, result);
          this.loadGridData();
          this.selectedSignal = null;
        }
      });
  }

  DeleteIcon(params: any) {
                    if (params.data.status === "No") {
                  this.snackBar.open(this.constants.deleterowMsg, "OK", { duration: 5000 });
                  return;
                }
    console.log("Deleting signal with id:", params.data.id);
    this.deleteSignal(params.data.id);
    this.loadGridData();
    this.getSignalCounts();
  }

  startWorkflow(params: any) {
    if(params.data.assignedUser == null || params.data.assignedUser == '' ){
       this.snackBar.open(
        "Please self assign before proceeding to workflow",
        "Ok",
        { duration: 3000 },
      );
      return;
    }
    // if (params.data.signalStatus !== "Signal") {
    //   this.snackBar.open(
    //     "Cannot proceed. The signal status is not marked as 'Signal'.",
    //     "Ok",
    //     { duration: 3000 },
    //   );
    //   return;
    // }
    this.showWorkflow = true;
    this.selectedSignal = params.data;
    console.log(this.selectedSignal);
    this.pageTitle = "Signal Review";
  }

  userAssign(params: any) {
                        if (params.data.status === "No") {
                  this.snackBar.open(this.constants.deleterowMsg, "OK", { duration: 5000 });
                  return;
                }
    console.log(params.data);
    // this.spinner.start();
    const currentUserId =  this.localStorageSettings.getSettings("UserId");

    this.assignUserToSignal(params.data.id, currentUserId);

    // const signals = JSON.parse(localStorage.getItem("signals") || "[]");

    // const index = signals.findIndex(
    //   (s: any) => s.signalId === params.data.signalId,
    // );

    // if (index !== -1) {
    //   signals[index].assignedUser = currentUser;
    //   signals[index].lastModifiedBy = currentUser;
    //   signals[index].lastModifiedAt = new Date();

    //   localStorage.setItem("signals", JSON.stringify(signals));

      // Refresh Grid Row
      // this.loadGridData();

      // this.snackBar.open("Signal assigned successfully", "Close", {
      //   duration: 3000,
      // });
    // }
    // this.spinner.stop();
  }

  onCreate() {
    this.dialog
      .open(CreateSignalPopupComponent, {
        maxWidth: "100vw",
        height: "95%",
        width: "90%",
        data: {
          mode: "create",
        },
        disableClose: true,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          console.log("New Signal Data:", result);
          this.createSignal(result);  
          this.loadGridData();
          this.getSignalCounts();
        }
      });
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    console.log("Grid is ready, API set:", !!this.gridApi); // Debug log

    // Set custom no rows template
    this.gridApi.setGridOption(
      "overlayNoRowsTemplate",
      '<span style="padding: 10px; font-size: 14px; color: #666;">' +
        this.noRowsMessage +
        "</span>",
    );

    // Load data after grid is ready
    // this.loadGridData();
  }

  autoSizeColumns() {
    if (this.gridApi) {
      setTimeout(() => {
        this.gridApi.sizeColumnsToFit();
      }, 100);
    }
  }

  onDownload() {
    if (!this.gridApi || !this.rowData || this.rowData.length === 0) {
      this.snackBar.open("No data to export", "Close", { duration: 3000 });
      return;
    }

    this.spinner.start();

    setTimeout(() => {
      this.gridApi.exportDataAsCsv({
        fileName: "Signals.csv",
      });
      this.spinner.stop();
    }, 500);
  }

  onRefresh() {
    this.loadGridData();
    this.getSignalCounts();
  }

  loadGridData() {
    this.spinner.start();

    // const storedSignals = localStorage.getItem("signals");

    // this.rowData = storedSignals ? JSON.parse(storedSignals) : [];
    // console.log("Loaded signals from localStorage:", this.rowData);

    this.spinner.start();
    this.jsignalsService
      .getAllSignals()
      .subscribe({
        next: (res: any) => {
          console.log(res);
          if(res.error == true){
            this.snackBar.open(res.statusMessage, "Ok", { duration: 3000 });
            this.rowData = [];
            this.spinner.stop();
            return;
          }
          // this.snackBar.open(res.statusMessage, "Ok", { duration: 3000 });
          this.rowData = res 
          if(this.queryData){
            let stateData=this.queryData
            let searchedRowData=this.rowData.filter(item=>item.signalId==stateData.signalId)[0]
            console.log(searchedRowData);
            
               if (searchedRowData.status === "No") {
                  this.snackBar.open(this.constants.deleterowMsg, "OK", { duration: 5000 });
                  return;
                }

            if (!searchedRowData.assignedUserId) {
              this.snackBar.open(
                "Please self assign before proceeding to workflow",
                "Ok",
                { duration: 3000 }
              );
              return;
            }

            if (searchedRowData.signalStatus == "COMPLETED") {
              this.snackBar.open(
                "Signal workflow already completed. No further actions allowed.",
                "Ok",
                { duration: 3000 }
              );
              return;
            }

            this.selectedSignal = searchedRowData;

            // ✅ Completed Steps
            let stepArray: number[] = [];

            if (this.selectedSignal.completedSteps) {
              stepArray = this.selectedSignal.completedSteps
                .split(',')
                .map((s: string) => +s);
            }

            this.completedSteps = stepArray.map((stepId: number) =>
              this.steps.findIndex(s => s.stepId === stepId)
            );

                          // Show workflow FIRST
              this.showWorkflow = true;

              // ✅ Determine active step properly
              let stepIndex = 0;

              // Case 1: If backend gives currentStepId
              if (this.selectedSignal.currentStepId) {
                const index = this.steps.findIndex(
                  s => s.stepId === this.selectedSignal.currentStepId
                );

                stepIndex = index !== -1 ? index : 0;
              }

              // Case 2: If currentStepId is NULL → derive from completedSteps
              else if (stepArray.length > 0) {
                const lastCompletedStepId = Math.max(...stepArray);

                const nextIndex = this.steps.findIndex(
                  s => s.stepId === lastCompletedStepId
                );

                // Move to next step
                stepIndex = nextIndex !== -1 ? nextIndex + 1 : 0;
              }

              // Case 3: nothing → default
              else {
                stepIndex = 0;
              }

              // ✅ Apply after render
              setTimeout(() => {
                this.activeIndex = stepIndex;
              });

              console.log("Resolved Active Step Index:", stepIndex);

            console.log("Active Step:", this.activeIndex);
            console.log("Completed Steps:", this.completedSteps);

            this.pageTitle = "Signal Review";
          }
          this.spinner.stop();
        },
        error: (err) => {
          console.error("error:", err);
          this.snackBar.open(err.error.statusMessage, "Ok", {
            duration: 3000,
          });
          this.rowData = [];
          this.spinner.stop();
        },
      });  



    // this.spinner.stop();

    if (this.gridApi) {
      if (this.rowData.length === 0) {
        this.gridApi.showNoRowsOverlay();
      } else {
        this.gridApi.hideOverlay();
      }
    }
  }

  onStepChange(index: number) {
    this.activeIndex = index;
  }
  cancelWorkflow() {
    this.showWorkflow = false;
    this.selectedSignal = null;
    this.pageTitle = "Signals";
    this.activeIndex = 0;
    this.completedSteps = [];
    this.selectedTabIndex = 0;
    this.isEditMode = false;
    this.validationErrors = [];
    this.queryData=undefined;
    this.loadGridData();
  }

  goToDecisionTab() {
    this.selectedTabIndex = 1; // Decision tab index
  }

  goToAttachmentTab() {
    this.selectedTabIndex = 2; // Attachment tab index
  }

  // COMPLETE BUTTON CLICK
  onCompleteClick() {
    this.validationErrors = [];

    const results: ValidationResult[] = [
      this.detailsComponent?.validateStep(),
      // this.decisionComponent?.validateStep(),
      this.attachmentComponent?.validateStep(),
    ];

    results.forEach((result) => {
      if (result && !result.valid) {
        this.validationErrors.push(...result.errors);
      }
    });
    console.log("errors :", this.validationErrors);

    // ❌ If Errors → Open Validation Dialog
    if (this.validationErrors.length > 0) {
      this.dialog.open(CommonWorkflowDialogComponent, {
        width: "500px",

        disableClose: true,
        autoFocus: false,
        data: {
          mode: "validation",
          errors: this.validationErrors,
        },
      });
      return;
    }

    // 🔴 Block navigation if edit mode & unsaved changes
    if (this.isEditMode) {
      const errors: ValidationError[] = [];
      errors.push({
        component: "Details",
        message: "Please save the edited details first",
      });

      this.dialog.open(CommonWorkflowDialogComponent, {
        width: "500px",

        disableClose: true,
        autoFocus: false,
        data: {
          mode: "validation",
          errors: errors,
        },
      });
      return;
    }

    // ✅ No Errors → Open Status Dialog
    this.openStatusDialog();
  }

  openStatusDialog() {
    const currentStep = this.steps[this.activeIndex];
    const currentStepId = currentStep?.stepId;
    const statusOptions = this.workflowSteps[currentStepId]?.options || [];

    const dialogRef = this.dialog.open(CommonWorkflowDialogComponent, {
      width: "500px",

      disableClose: true,
      autoFocus: false,
      data: {
        mode: "status",
        statusOptions: statusOptions,
      },
    });

    dialogRef.afterClosed().subscribe((selectedStatus) => {
      if (!selectedStatus) return;

      const selectedOption = statusOptions.find(
        (opt) => opt.value === selectedStatus,
      );

      if (!selectedOption) return;

      //Backend Call
      this.completeWorkflowStep(currentStepId, selectedStatus , selectedOption.nextStep);
    });
  }

  completeWorkflowStep(stepId: any, status: string, nextStepId: any) {
    const payload = {
    nextState: status,
    nextStepId: nextStepId
  };

  this.spinner.start();

  this.jsignalsService
    .changeSignalStatus(this.selectedSignal.id, payload)
    .subscribe({
      next: (res: any) => {

        if (res.error) {
          this.snackBar.open(res.statusMessage, "Ok", { duration: 3000 });
          this.spinner.stop();
          return;
        }

        this.snackBar.open(res.statusMessage, "Close", { duration: 3000 });

        //RELOAD DATA FROM BACKEND 
        this.refreshSelectedSignal();

        this.getSignalCounts();

        this.spinner.stop();
      },
      error: (err) => {
        console.error(err);
        this.snackBar.open("Error updating workflow", "Ok", { duration: 3000 });
        this.spinner.stop();
      }
    });

  }

  createSignal(signalData: any) {
    const createPayload = [signalData];
    console.log("Creating signal with data:", createPayload);
  this.spinner.start();
  this.jsignalsService
      .createSignal(createPayload)
      .subscribe({
        next: (res: any) => {
          console.log(res);
          if(res.error == true){
            this.snackBar.open(res.statusMessage, "Ok", { duration: 3000 });
            this.getSignalCounts();
            this.spinner.stop();
            return;
          }
          this.snackBar.open(res.statusMessage, "Ok", { duration: 3000 });
          this.spinner.stop();
          this.getSignalCounts();
          this.loadGridData();
        },
        error: (err) => {
          console.error("error:", err);
          this.snackBar.open(err.error.statusMessage, "Ok", {
            duration: 3000,
          });
          this.getSignalCounts();
          this.spinner.stop();
          this.loadGridData();
        },
      });  
  }

  updateSignal(id:any,signalData: any) {
    console.log("Updating signal with data:", signalData , id);
     this.spinner.start();
    this.jsignalsService
      .updateSignal(id,signalData)
      .subscribe({
        next: (res: any) => {
          console.log(res);
          if(res.error == true){
            this.snackBar.open(res.statusMessage, "Ok", { duration: 3000 });
            this.spinner.stop();
            return;
          }
          this.snackBar.open(res.statusMessage, "Ok", { duration: 3000 });
          this.spinner.stop();
          this.loadGridData();
        },
        error: (err) => {
          console.error("error:", err);
          this.snackBar.open(err.error.statusMessage, "Ok", {
            duration: 3000,
          });
          this.spinner.stop();
          this.loadGridData();
        },
      });  
  }

  deleteSignal(id:any) {
    console.log("Deleting signal with id:", id);
    this.spinner.start();
    this.jsignalsService
      .deleteSignal(id)
      .subscribe({
        next: (res: any) => {
          console.log(res);
          if(res.error == true){
            this.snackBar.open(res.statusMessage, "Ok", { duration: 3000 });
            this.getSignalCounts();
            this.spinner.stop();
            return;
          }
          this.snackBar.open(res.statusMessage, "Ok", { duration: 3000 });
          this.getSignalCounts();
          this.loadGridData();
          this.spinner.stop();
        },
        error: (err) => {
          console.error("error:", err);
          this.snackBar.open(err.error.statusMessage, "Ok", {
            duration: 3000,
          });
          this.spinner.stop();
          this.loadGridData();
        },
      });  
  }

  assignUserToSignal(signalId:any, userId:any) {
    console.log("Assigning user to signal with id:", signalId, userId);
    this.spinner.start();
    this.jsignalsService
      .assignUserToSignal(signalId, userId)
      .subscribe({
        next: (res: any) => {
          console.log(res);
          if(res.error == true){
            this.snackBar.open(res.statusMessage, "Ok", { duration: 3000 });
            this.spinner.stop();
             this.loadGridData();
            return;
          }
          this.snackBar.open(res.statusMessage, "Ok", { duration: 3000 });
           this.loadGridData();
          this.spinner.stop();
          
        },
        error: (err) => {
          console.error("error:", err);
          this.snackBar.open(err.error.statusMessage, "Ok", {
            duration: 3000,
          });
          this.spinner.stop();
          this.loadGridData();
        },
      });  
  }

  refreshSelectedSignal() {
  this.jsignalsService.getSignalById(this.selectedSignal.id).subscribe((res: any) => {

     if(res.error == true){
            this.snackBar.open(res.statusMessage, "Ok", { duration: 3000 });
            this.spinner.stop();
            return;
          }

    this.selectedSignal = res;

    if(this.selectedSignal?.signalStatus === "COMPLETED"){
      console.log("Signal workflow completed. Closing review view.");
      this.cancelWorkflow();
      return;
    }

    // ✅ Completed Steps
    let stepArray: number[] = [];

    if (res.completedSteps) {
      stepArray = res.completedSteps.split(',').map((s: string) => +s);
    }

    this.completedSteps = stepArray.map((stepId: number) =>
      this.steps.findIndex(s => s.stepId === stepId)
    );

    // ✅ Active Step Logic (handles NULL also)
    let stepIndex = 0;

    if (res.currentStepId) {
      const index = this.steps.findIndex(
        s => s.stepId === res.currentStepId
      );
      stepIndex = index !== -1 ? index : 0;

    } else if (stepArray.length > 0) {
      const lastCompleted = Math.max(...stepArray);

      const nextIndex = this.steps.findIndex(
        s => s.stepId === lastCompleted
      );

      if (nextIndex !== -1 && nextIndex + 1 < this.steps.length) {
        stepIndex = nextIndex + 1;
      } else {
        stepIndex = nextIndex !== -1 ? nextIndex : 0;
      }
    }

    this.activeIndex = stepIndex;

  });
}

getSignalCounts(){
    this.spinner.start();
    this.jsignalsService
      .getSignalCounts()
      .subscribe({
        next: (res: any) => {
          console.log(res);
          if(res.error == true){
            this.snackBar.open(res.statusMessage, "Ok", { duration: 3000 });
            this.spinner.stop();
            //  this.loadGridData();
            return;
          }
          // this.snackBar.open(res.statusMessage, "Ok", { duration: 3000 });
          //  this.loadGridData();
          console.log("Signal Counts:", res);
          this.totalCount = res.totalSignals || 0;
          this.totaldetectedCount = res.detectedCurrent || 0;
          this.totalSignalValidated = res.validationCurrent || 0;
          this.signalAssessed = res.assessmentCurrent || 0;
          this.signalCommunicated = res.communicationCurrent || 0;
          this.spinner.stop();
          
        },
        error: (err) => {
          console.error("error:", err);
          this.snackBar.open(err.error.statusMessage, "Ok", {
            duration: 3000,
          });
          this.spinner.stop();
          this.loadGridData();
        },
      });  
}

}
